import type { ProductCode } from '@data/config/reports';
import type { Drilldown } from '@data/components/report/ReportFilterBar/ReportFilterBar';
import type { MetricV2Type } from '@data/config/metrics';

/**
 * One metric and the conditions under which it is asked for.
 *
 * Generic over the metric name so the admin roll-ups, whose names are a
 * separate union, share this shape and the resolver below.
 */
export interface MetricRule<Metric extends string = MetricV2Type> {
  metric: Metric;
  /** Which product codes this metric applies to. Omit = all products. */
  products?: ProductCode[];
  /** Which report levels this metric applies to. Omit = all levels. */
  drilldowns?: Drilldown[];
  /**
   * Product codes that, when they are the ONLY codes the user holds for this
   * app, cause this metric to be excluded.
   *
   * Example: `excludedByProductCodes: ['R4RWB']` means the metric is hidden
   * when the user's R4R products consist solely of R4RWB.
   */
  excludedByProductCodes?: string[];
}

/**
 * Resolves a list of metric rules into concrete metric names based on the
 * current product and drilldown context.
 *
 * Rules without `products` apply to all products.
 * Rules without `drilldowns` apply to all drilldown levels.
 *
 * @param userProductCodes - Optional array of the user's product codes for the
 *   current application. Used to evaluate `excludedByProductCodes` on each rule.
 */
export function resolveMetrics<Metric extends string>(
  rules: MetricRule<Metric>[],
  productId: ProductCode,
  drilldown: Drilldown,
  userProductCodes?: string[]
): Metric[] {
  return rules
    .filter((rule) => {
      // Filter by app-level product
      if (rule.products && !rule.products.includes(productId)) return false;
      // Filter by drilldown level
      if (rule.drilldowns && !rule.drilldowns.includes(drilldown)) return false;
      // Filter by product-code-level exclusion
      if (
        rule.excludedByProductCodes &&
        userProductCodes &&
        userProductCodes.length > 0
      ) {
        const hasOnlyExcludedProducts = userProductCodes.every((code) =>
          rule.excludedByProductCodes!.includes(code)
        );
        if (hasOnlyExcludedProducts) return false;
      }
      return true;
    })
    .map((rule) => rule.metric);
}
